/**
 * GlobalController — the persistent global-transform axes on `[ ] < >`.
 *
 * Unlike effects (momentary) and the +/- modifier (per-held-key), these two axes
 * are *global* and *persistent*: whatever you scrub them to stays put until you
 * scrub back or reset. There are exactly two:
 *
 *   global.zoom   [ out / ] in       — a camera dolly on the whole field
 *   global.speed  < slower / > faster (below neutral runs in reverse)
 *
 * Each key is a fixed direction on a fixed axis (no "target key" concept like the
 * modifier). A plain tap nudges; a hold ramps continuously — the same feel as the
 * +/- knob, and it reuses that ramp math. Values live on the bus in the same
 * convention as modifiers: 0..1, neutral 0.5, clamped. The renderer maps zoom to a
 * uv scale (the uGlobalZoom uniform) and speed to a virtual-time multiplier.
 *
 * Bus-only writes; it never touches the renderer and never sees a KeyboardEvent
 * beyond the normalized key identity handed to keyDown/keyUp.
 */

import { ParamBus } from '../state/paramBus';
import { MODIFIER_RATE } from './modifierController';

/** paramBus keys for the two global axes. */
export const GLOBAL_ZOOM = 'global.zoom';
export const GLOBAL_SPEED = 'global.speed';

/** Neutral value for zoom — 0.5 means "1x" (no zoom). */
export const GLOBAL_DEFAULT = 0.5;

/**
 * Default for the speed axis. The renderer maps speed symmetrically around 0.5
 * (0.5 = a full freeze, the ends equal-and-opposite), so "normal 1x forward" sits
 * at 2/3. The app opens and Esc-resets here, not at the frozen midpoint.
 */
export const GLOBAL_SPEED_DEFAULT = 2 / 3;

/** Key code -> { param, dir } for the four global keys (see keyState.normalizeKey). */
const AXIS_KEYS: ReadonlyMap<string, { param: string; dir: number }> = new Map([
  ['BracketLeft', { param: GLOBAL_ZOOM, dir: -1 }],
  ['BracketRight', { param: GLOBAL_ZOOM, dir: +1 }],
  ['Comma', { param: GLOBAL_SPEED, dir: -1 }],
  ['Period', { param: GLOBAL_SPEED, dir: +1 }],
]);

export class GlobalController {
  /** -1 until the first tick seeds the timebase (0 is a valid timestamp). */
  private lastMs = -1;
  /** Currently-held global key codes. */
  private readonly heldKeys = new Set<string>();

  constructor(private readonly bus: ParamBus) {}

  /** Feed a normalized key-down (same identity KeyState/InputController use). */
  keyDown(key: string): void {
    if (AXIS_KEYS.has(key)) this.heldKeys.add(key);
  }

  /** Feed a normalized key-up. */
  keyUp(key: string): void {
    this.heldKeys.delete(key);
  }

  /**
   * Advance the ramp. `nowMs` is a monotonic clock (performance.now); the first
   * call just seeds the timebase so a long first frame can't jump the value. Each
   * axis moves by the net direction of its held keys (both held = cancel).
   */
  tick(nowMs: number): void {
    const dt = this.lastMs < 0 ? 0 : (nowMs - this.lastMs) / 1000;
    this.lastMs = nowMs;
    if (dt <= 0 || this.heldKeys.size === 0) return;

    for (const param of [GLOBAL_ZOOM, GLOBAL_SPEED]) {
      const dir = this.direction(param);
      if (dir === 0) continue;
      const cur = this.bus.get(param, defaultFor(param));
      const next = clamp01(round2(cur + dir * MODIFIER_RATE * dt));
      this.bus.set(param, next);
    }
  }

  /**
   * Which axis a +/- indicator should show right now: the param of the currently
   * held global key (zoom wins if both a zoom and a speed key are held), or
   * undefined when none is. Lets the dock reuse the −/+ modify-label for these axes.
   */
  activeAxis(): string | undefined {
    let zoom = false;
    let speed = false;
    for (const key of this.heldKeys) {
      const axis = AXIS_KEYS.get(key);
      if (axis?.param === GLOBAL_ZOOM) zoom = true;
      else if (axis?.param === GLOBAL_SPEED) speed = true;
    }
    return zoom ? GLOBAL_ZOOM : speed ? GLOBAL_SPEED : undefined;
  }

  /** Both axes back to their defaults (zoom 1x, speed 1x forward) — the Esc panic-reset. */
  reset(): void {
    this.bus.set(GLOBAL_ZOOM, GLOBAL_DEFAULT);
    this.bus.set(GLOBAL_SPEED, GLOBAL_SPEED_DEFAULT);
  }

  /** Net +1 / -1 / 0 direction for one axis from its currently-held keys. */
  private direction(param: string): number {
    let dir = 0;
    for (const key of this.heldKeys) {
      const axis = AXIS_KEYS.get(key);
      if (axis?.param === param) dir += axis.dir;
    }
    return dir === 0 ? 0 : dir > 0 ? 1 : -1;
  }
}

/** Per-axis neutral: speed rests at 1x forward (2/3), zoom at 1x (0.5). */
function defaultFor(param: string): number {
  return param === GLOBAL_SPEED ? GLOBAL_SPEED_DEFAULT : GLOBAL_DEFAULT;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
